import {Component, computed, inject, input, signal} from '@angular/core';
import { CommonModule } from '@angular/common';
import {IItem} from '../../../../../types/IItem';
import {IconsComponent} from '../../../../../shared/components/icons/icons.component';
import {ItemImageComponent} from '../../../../../shared/components/item-image/item-image.component';
import {BadgeComponent} from '../../../../../shared/components/badge/badge.component';
import {ResultsStore} from '../../../../../shared/store/results.store';
import {background, containerClasses, stars} from '../../../../../shared/utils/comparison.utils';
import {ResultsItemSupplierSelectionModal} from '../../results-item-supplier-selection-modal/results-item-supplier-selection-modal';
import {Spinner} from '../../../../../shared/components/spinner/spinner';
import {AppStore} from '../../../../../shared/store/app.store';
import {ButtonComponent} from '../../../../../shared/components/button/button.component';
import {weightedCost, weightedPrice} from '../../../../../shared/utils/item.utils';
import {ResultTableService} from '../../../services/result-table.service';
import {SettingsService} from '../../../../../shared/services/settings.service';

@Component({
  selector: 'app-results-footer-compare-card',
  standalone: true,
  imports: [CommonModule, IconsComponent, ItemImageComponent, BadgeComponent, Spinner, ButtonComponent],
  templateUrl: './results-footer-compare-card.component.html',
  styleUrl: './results-footer-compare-card.component.css'
})
export class ResultsFooterCompareCardComponent {
  level = input.required<'good' | 'better' | 'best'>();
  item = input.required<IItem>();

  resultsStore = inject(ResultsStore);
  private settings = inject(SettingsService);
  private resultTableService = inject(ResultTableService);

  hasQuantitySourced = computed(() => this.resultsStore.quantity() === this.quantity());

  quantity = computed(() => this.item().sourcing.reduce((carry, {selected_quantity}) => carry + selected_quantity, 0));

  pricing = computed(() => this.resultsStore.getAddonsForItem()(this.item().id, weightedPrice(this.item()), this.quantity())?.total_after_addons || 0);

  local = computed(() => {
    const sources = this.item().sourcing?.filter(s => s.type === 'local') || [];
    return {
      source: sources[0] ?? null,
      total: sources.reduce((sum, s) => sum + (s.selected_quantity ?? 0), 0)
    };
  });

  transfer = computed(() => {
    const sources = this.item().sourcing?.filter(s => s.type === 'transfer') || [];
    return {
      sources,
      total: sources.reduce((sum, s) => sum + (s.selected_quantity ?? 0), 0)
    };
  });

  distributor = computed(() => {
    const sources = this.item().sourcing?.filter(s => s.type === 'distributor') || [];
    return {
      sources,
      total: sources.reduce((sum, s) => sum + (s.selected_quantity ?? 0), 0)
    };
  });

  external = computed(() => {
    const sources = this.item().sourcing?.filter(s => s.type === 'external') || [];
    return {
      source: sources[0] ?? null,
      total: sources.reduce((sum, s) => sum + (s.selected_quantity ?? 0), 0)
    };
  });

  protected orderedBadges = computed(() => {
    const badges = [
      {type: 'local', data: this.local(), variant: 'green' as const, icon: 'garage'},
      {type: 'transfer', data: this.transfer(), variant: 'blue' as const, icon: 'arrow-left-arrow-right'},
      {type: 'distributor', data: this.distributor(), variant: 'red' as const, icon: 'truck'},
      {type: 'external', data: this.external(), variant: 'purple' as const, icon: 'cart-plus'}
    ];

    const orderMap = new Map<string, number>(
      this.settings.get('availability_order').value.map((item: any) => [item.type, item.order])
    );

    return badges
      .filter(badge => badge.data.total)
      .sort((a, b) => {
        const aOrder = orderMap.get(a.type) ?? 999;
        const bOrder = orderMap.get(b.type) ?? 999;
        return aOrder - bOrder;
      });
  });

  remove() {
    this.resultsStore.removeCompareItem(this.item());
  }

  openSupplierModal() {
    this.resultTableService.addToCompare(this.item());
  }

  protected readonly containerClasses = containerClasses;
  protected readonly background = background;
  protected readonly stars = stars;
}
